/* eslint-disable max-len */
/* eslint-disable no-undef */
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import Script from 'react-load-script';
import classNames from 'classnames';
import { useScreenWidth } from '@/hooks';
import styles from './Map.module.scss';
import Image from 'next/image';
import { MAP_PIN_SVG } from '@/constants';
import { ColorField } from '@prismicio/client';
import { omit } from 'lodash';

export interface ILocation {
  label: string;
  lat: number;
  lng: number;
  title: string;
  pin?: string;
  photo?: string;
  google_map_url?: string;
  url_slug?: string;
}

export interface IMarkerStyle {
  titleColor: ColorField;
  deaultMarkerColor: ColorField;
}

interface IMapsProps {
  actionClickMarker?: Dispatch<SetStateAction<google.maps.Marker>>;
  centered?: 'center' | 'left' | 'SE' | 'rigth';
  className?: string;
  hideInterestingPoints?: boolean;
  locations: ILocation[];
  openInfo?: boolean;
  overlay?: boolean;
  pinColor?: ColorField;
  darkMode?: boolean;
  defaultMarker?: string;
  markerStyle?: IMarkerStyle;
  zoom?: number;
  isLocation?: boolean;
  showTitle?: boolean;
}

const Maps: FC<IMapsProps> = ({
  actionClickMarker,
  locations,
  className,
  centered = 'center',
  hideInterestingPoints = false,
  openInfo = true,
  overlay = false,
  showTitle = true,
  pinColor,
  darkMode,
  defaultMarker,
  markerStyle,
  isLocation,
  zoom = 12,
}) => {
  const { width } = useScreenWidth();
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLElement>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

  const getOffset = () => {
    if (width < 420) return 0;
    if (width < 768) return 50;
    if (width < 900) return 350;
    if (width < 1286) return 450;
    if (width < 1536) return 550;
    return 700;
  };

  const getPanOffset = () => {
    const offsets = {
      rigth: {
        0: { x: 0, y: -100 },
        640: { x: 0, y: -130 },
        768: { x: -140, y: 0 },
        1024: { x: -200, y: 0 },
        1280: { x: -260, y: 0 },
        1536: { x: -420, y: 0 },
        default: { x: 0, y: 0 },
      },
      center: {
        0: { x: 0, y: 0 },
        640: { x: 0, y: 0 },
        768: { x: 0, y: 0 },
        1024: { x: 0, y: 0 },
        1280: { x: 0, y: 0 },
        1536: { x: 0, y: 0 },
        default: { x: 0, y: 0 },
      },
      SE: {
        0: { x: 0, y: -200 },
        640: { x: -120, y: 0 },
        768: { x: -160, y: 0 },
        1024: { x: -200, y: 0 },
        1280: { x: 0, y: 0 },
        1536: { x: 0, y: 0 },
        default: { x: 0, y: 0 },
      },
      left: {
        0: { x: 0, y: -150 },
        640: { x: -160, y: 0 },
        768: { x: -140, y: 0 },
        1024: { x: 0, y: -140 },
        1280: { x: -100, y: -160 },
        1536: { x: -150, y: -50 },
        default: { x: -500, y: 200 },
      },
    };

    const breakpoints = [1536, 1280, 1024, 768, 640, 0];
    const current = breakpoints.find(bp => width >= bp) ?? 0;
    return offsets[centered][current] || offsets[centered].default;
  };

  const createMarker = (
    lat: number,
    lng: number,
    label: string,
    map: google.maps.Map,
    bounds?: google.maps.LatLngBounds,
    pin?: string,
    locationImage?: string,
    google_map_url?: string,
    isLocation?: boolean,
  ) => {
    const labelColor = markerStyle?.titleColor ?? '#000000';
    const maxWidth = isLocation ? '180px' : '150px';
    const imageUrl = locationImage ?? 'images/product-placeholder.svg';
    const mapUrl = google_map_url ?? '/';
    const fillColor = markerStyle?.deaultMarkerColor as string;

    const basePin = pin
      ? {
          scaledSize: new google.maps.Size(40, 50),
          url: pin,
          labelOrigin: new google.maps.Point(15, 66),
        }
      : {
          anchor: new google.maps.Point(15, 30),
          labelOrigin: new google.maps.Point(15, 43),
          ...{
            ...(pinColor
              ? { ...omit(MAP_PIN_SVG, 'fillColor'), fillColor: pinColor }
              : { ...MAP_PIN_SVG, fillColor }),
          },
        };

    const marker = new google.maps.Marker({
      icon: basePin,
      map,
      position: { lat, lng },
      title: showTitle ? label : '',
      label: showTitle
        ? {
            text: label,
            color: labelColor,
            fontSize: '12px',
            fontWeight: 'bold',
          }
        : '',
    });

    marker.addListener('click', () => {
      if (actionClickMarker) actionClickMarker(marker);

      if (openInfo) {
        if (infoWindowRef.current) infoWindowRef.current.close();

        const contentHTML = `
          <div style="font-size: 14px; color: #333; padding-top: 10px; min-width: 120px; max-width: ${maxWidth}">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
              <h3 style="margin: 0; font-size: 14px; font-weight:500; line-height: 20px; text-transform: uppercase">${label}</h3>
              <button id="closeButton" class="gm-ui-hover-effect" style="background: none; border: none; font-size: 16px; cursor: pointer;">✖</button>
            </div>
            <a href="${mapUrl}" target="_blank">
              <img src="${imageUrl}" alt="${label}" style="width: 100%; aspect-ratio: 1/1;" />
            </a>
          </div>
        `;

        const info = new google.maps.InfoWindow({ content: contentHTML });
        infoWindowRef.current = info;
        info.open(map, marker);

        google.maps.event.addListenerOnce(info, 'domready', () => {
          document
            .getElementById('closeButton')
            ?.addEventListener('click', () => {
              info.close();
            });
        });
      }
    });

    try {
      bounds?.extend(marker.getPosition() as google.maps.LatLng);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const apiLoaded =
      Script.loadedScripts[process.env.NEXT_PUBLIC_GOOGLE_MAP_API];
    if (!locations.length || !apiLoaded) return;

    const { lat, lng } = locations[0];
    const isSingle = locations.length === 1;

    const map = new google.maps.Map(ref.current as HTMLElement, {
      disableDefaultUI: true,
      zoom,
      center: { lat, lng },
      ...(hideInterestingPoints && {
        styles: [
          { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi.park', stylers: [{ visibility: 'off' }] },
          {
            featureType: 'poi.place_of_worship',
            stylers: [{ visibility: 'off' }],
          },
          { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
          {
            featureType: 'poi.sports_complex',
            stylers: [{ visibility: 'off' }],
          },
        ],
      }),
      ...(overlay && {
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { elementType: 'geometry', stylers: [{ color: '#333333' }] },
          {
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#333333' }],
          },
          { elementType: 'labels.text.fill', stylers: [{ color: '#686868' }] },
          {
            elementType: 'labels.text.fill',
            featureType: 'administrative.locality',
            stylers: [{ color: '#686868' }],
          },
          {
            elementType: 'geometry',
            featureType: 'poi.park',
            stylers: [{ color: '#263c3f' }],
          },
          {
            elementType: 'labels.text.fill',
            featureType: 'poi.park',
            stylers: [{ color: '#6b9a76' }],
          },
          {
            elementType: 'geometry',
            featureType: 'road',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'geometry.stroke',
            featureType: 'road',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'labels.text.fill',
            featureType: 'road',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'geometry',
            featureType: 'road.highway',
            stylers: [{ color: '#746855' }],
          },
          {
            elementType: 'geometry.stroke',
            featureType: 'road.highway',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'labels.text.fill',
            featureType: 'road.highway',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'geometry',
            featureType: 'transit',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'labels.text.fill',
            featureType: 'transit.station',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'geometry',
            featureType: 'water',
            stylers: [{ color: '#2b2b2b' }],
          },
          {
            elementType: 'labels.text.fill',
            featureType: 'water',
            stylers: [{ color: '#515c6d' }],
          },
          {
            elementType: 'labels.text.stroke',
            featureType: 'water',
            stylers: [{ color: '#2b2b2b' }],
          },
        ],
      }),
      ...(darkMode && {
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
          { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
          {
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#f5f5f5' }],
          },
          {
            featureType: 'administrative.land_parcel',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#bdbdbd' }],
          },
          {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [{ color: '#eeeeee' }],
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#757575' }],
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#e5e5e5' }],
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9e9e9e' }],
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#ffffff' }],
          },
          {
            featureType: 'road.arterial',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#757575' }],
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#dadada' }],
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#616161' }],
          },
          {
            featureType: 'road.local',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9e9e9e' }],
          },
          {
            featureType: 'transit.line',
            elementType: 'geometry',
            stylers: [{ color: '#e5e5e5' }],
          },
          {
            featureType: 'transit.station',
            elementType: 'geometry',
            stylers: [{ color: '#eeeeee' }],
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#c9c9c9' }],
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9e9e9e' }],
          },
        ],
      }),
    });

    const bounds = new google.maps.LatLngBounds();

    locations.forEach(location => {
      createMarker(
        location.lat,
        location.lng,
        location.label,
        map,
        isSingle ? undefined : bounds,
        defaultMarker ?? location.pin,
        location.photo,
        location.google_map_url,
        isLocation,
      );
    });

    if (isSingle) {
      setTimeout(() => {
        const { x, y } = getPanOffset();
        map.panBy(x, y);
      }, 200);
    }

    if (!isSingle) {
      const padding = {
        top: 100,
        right: 50,
        bottom: 50,
        left: getOffset(),
      };

      if (centered === 'center') {
        map.fitBounds(bounds);
      }

      if (centered !== 'center') {
        map.fitBounds(bounds, padding);
      }
    }
  }, [loading, locations, width]);

  return (
    <figure
      className={classNames(styles['map-multilocation'], className)}
      ref={ref}
    >
      {locations.length ? (
        !Script.loadedScripts[process.env.NEXT_PUBLIC_GOOGLE_MAP_API] && (
          <Script
            onLoad={() => setLoading(false)}
            url={process.env.NEXT_PUBLIC_GOOGLE_MAP_API}
          />
        )
      ) : (
        <>
          <Image
            alt="Default map"
            className={styles.default_map}
            fill
            sizes="100vw"
            src={
              darkMode
                ? `${REVERSE_PROXY_LINK}/images/default_map_dark.png`
                : `${REVERSE_PROXY_LINK}/images/default_map.png`
            }
          />
          <div className={styles.empty_locations_square}>
            <i
              className={classNames('icon icon_search', styles.search_icon)}
            ></i>
            <span className={styles.title}>No stores available</span>
            <p className={styles.message}>
              There are no available stores in this area. Please try again to
              find a new store
            </p>
          </div>
        </>
      )}
    </figure>
  );
};

export default Maps;
